<script setup lang="ts">
import type { MemoryTrace } from '../../../libs/velis-memory'
import type { ChatHistoryItem } from '../../../types/chat'

import { Button } from '@proj-airi/ui'
import { useMediaQuery } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import { useChatConnection } from '../../../composables/use-chat-connection'
import { acceptanceSurfacePublisherKey } from '../../../libs/acceptance-surface'
import {
  buildWorkbenchExport,
  classifyWorkbenchTool,
} from '../../../libs/agent-workbench'
import { workbenchMcpReaderKey } from '../../../libs/agent-workbench-context'
import { injectKeyPiniaSynced } from '../../../libs/pinia/synced-context'
import { attentionBadcaseKinds, formatAttentionTrace } from '../../../libs/velis-attention'
import { formatCapabilityTrace } from '../../../libs/velis-capability'
import { attentionBadcaseState, attentionTraceState } from '../../../libs/velis-attention-session'
import { latencyMetricNames } from '../../../libs/velis-latency'
import { getVelisMemoryGateway, latestVelisMemoryActivity } from '../../../libs/velis-memory-bridge'
import { perceptionPermissionState, perceptionTraceState, setPerceptionPermission } from '../../../libs/velis-perception-session'
import { projectAgentTasks } from '../../../libs/velis-runtime-snapshot'
import { useLlmToolsStore } from '../../../stores/ai/chat-llm/tools'
import { useChatSessionStore } from '../../../stores/chat/session-store'
import { useAiriCardStore } from '../../../stores/modules/airi-card'
import { useHearingStore } from '../../../stores/modules/hearing'
import { useSpeechStore } from '../../../stores/modules/speech'
import { useVelisCapabilityTraceStore } from '../../../stores/velis-capability-trace'
import { useVelisLatencyStore } from '../../../stores/velis-latency'

const { t } = useI18n()
const narrow = useMediaQuery('(max-width: 960px)')
const panel = ref<'sessions' | 'timeline' | 'diagnosis'>('timeline')
const expandedId = ref('')
const exportArmed = ref(false)
const exportNotice = ref('')
const selectedSessionId = ref('')
const isLeader = ref(false)
const participantCount = ref(0)
const synced = inject(injectKeyPiniaSynced, null)
const mcpReader = inject(workbenchMcpReaderKey, null)
const acceptanceSurface = inject(acceptanceSurfacePublisherKey, null)
let stopLeadership: (() => void) | undefined
let stopCoordination: (() => void) | undefined

const connection = useChatConnection()
const sessionStore = useChatSessionStore()
const toolsStore = useLlmToolsStore()
const cardStore = useAiriCardStore()
const speechStore = useSpeechStore()
const latencyStore = useVelisLatencyStore()
const { turns: latencyTurns, voiceWarm } = storeToRefs(latencyStore)
const latencyNewest = computed(() => [...latencyTurns.value].reverse())
const hearingStore = useHearingStore()
const { activeSessionId, messages, sessionMessages, sessionMetas } = storeToRefs(sessionStore)
const { activeCard, activeCardId } = storeToRefs(cardStore)
const { activeSpeechProvider, activeSpeechModel, activeSpeechVoiceId, configured: speechReady } = storeToRefs(speechStore)
const { activeTranscriptionProvider, activeTranscriptionModel, configured: hearingReady } = storeToRefs(hearingStore)
const { tools } = storeToRefs(toolsStore)
const mcpServers = ref<Array<{ name: string, state: string }>>([])
const mcpUnavailable = ref(false)
const memoryEntries = ref<Array<MemoryTrace & { error?: string }>>([])
const memoryUnavailable = ref(false)
const perceptionPermissions = perceptionPermissionState()
const perceptionTraces = perceptionTraceState()
const attentionTraces = attentionTraceState()
const attentionBadcases = attentionBadcaseState()
const capabilityTraceStore = useVelisCapabilityTraceStore()
const { traces: capabilityTraces } = storeToRefs(capabilityTraceStore)

const sessions = computed(() => Object.values(sessionMetas.value).sort((left, right) => right.updatedAt - left.updatedAt))
const viewingSessionId = computed(() => selectedSessionId.value || activeSessionId.value)
const viewingMessages = computed<ChatHistoryItem[]>(() => {
  const sessionId = viewingSessionId.value
  if (!sessionId)
    return []
  if (sessionId === activeSessionId.value)
    return messages.value
  return sessionMessages.value[sessionId] ?? []
})
const sessionLoaded = computed(() => {
  const sessionId = viewingSessionId.value
  if (!sessionId)
    return false
  return sessionId === activeSessionId.value || sessionMessages.value[sessionId] !== undefined
})
const tasks = computed(() => projectAgentTasks(viewingSessionId.value, viewingMessages.value))
const toolFacts = computed(() => tools.value.map(tool => classifyWorkbenchTool(tool.function.name)))
const lastFailure = computed(() => [...tasks.value].reverse().find((task) => {
  return task.phase === 'failed' || task.phase === 'timed-out' || task.phase === 'rejected' || task.phase === 'cancelled'
}))

watch(panel, (value) => {
  void acceptanceSurface?.publish({ workbenchPanel: value })
}, { immediate: true })

watch(activeSessionId, (sessionId) => {
  if (!selectedSessionId.value)
    selectedSessionId.value = sessionId
}, { immediate: true })

onMounted(() => {
  void loadMcp()
  void loadMemoryActivity()
  if (!synced)
    return
  isLeader.value = synced.isLeader()
  participantCount.value = synced.getParticipantCount()
  stopLeadership = synced.onLeadershipChange((value) => {
    isLeader.value = value
  })
  stopCoordination = synced.onCoordinationChange((coordination) => {
    participantCount.value = coordination.participantCount
  })
})

onUnmounted(() => {
  stopLeadership?.()
  stopCoordination?.()
})

function showPanel(next: 'sessions' | 'timeline' | 'diagnosis') {
  panel.value = next
}

function selectSession(sessionId: string) {
  selectedSessionId.value = sessionId
  expandedId.value = ''
  if (narrow.value)
    panel.value = 'timeline'
}

function toggleEvent(id: string) {
  expandedId.value = expandedId.value === id ? '' : id
}

async function loadMcp() {
  if (!mcpReader) {
    mcpUnavailable.value = true
    mcpServers.value = []
    return
  }
  try {
    mcpServers.value = await mcpReader.read()
    mcpUnavailable.value = false
  }
  catch {
    mcpServers.value = []
    mcpUnavailable.value = true
  }
}

watch(latestVelisMemoryActivity, () => {
  void loadMemoryActivity()
})

async function loadMemoryActivity() {
  const gateway = getVelisMemoryGateway()
  if (!gateway) {
    memoryUnavailable.value = true
    memoryEntries.value = []
    return
  }
  try {
    const result = await gateway.activity()
    memoryEntries.value = result.entries
    memoryUnavailable.value = result.error !== undefined
  }
  catch {
    memoryEntries.value = []
    memoryUnavailable.value = true
  }
}

function togglePerception(kind: 'browser' | 'screen') {
  const next = perceptionPermissions.value[kind] === 'off' ? 'on-demand' : 'off'
  setPerceptionPermission(kind, next)
}

function formatTime(at?: number) {
  if (typeof at !== 'number')
    return t('settings.pages.agent-workbench.none')
  return new Date(at).toLocaleString()
}

function formatDuration(durationMs?: number) {
  if (typeof durationMs !== 'number')
    return t('settings.pages.agent-workbench.none')
  return `${durationMs} ms`
}

function exportStatus(phase: string) {
  if (phase === 'timed-out')
    return 'timeout' as const
  if (phase === 'succeeded')
    return 'success' as const
  if (phase === 'started' || phase === 'running')
    return 'running' as const
  if (phase === 'pending-confirmation')
    return 'pending-confirmation' as const
  if (phase === 'rejected')
    return 'rejected' as const
  if (phase === 'cancelled')
    return 'cancelled' as const
  return 'failed' as const
}

async function copyExport() {
  const text = buildWorkbenchExport({
    sessionId: viewingSessionId.value,
    provider: connection.providerLabel.value,
    model: connection.modelLabel.value,
    phase: connection.phase.value,
    events: tasks.value.map(task => ({
      id: task.id,
      at: task.startedAt,
      kind: task.kind === 'tool' ? 'tool' as const : 'reply' as const,
      status: exportStatus(task.phase),
      summary: task.summary,
      toolName: task.toolName,
    })),
  })
  try {
    await navigator.clipboard.writeText(text)
    exportNotice.value = t('settings.pages.agent-workbench.export-copied')
    exportArmed.value = false
  }
  catch {
    exportNotice.value = t('settings.pages.agent-workbench.export-failed')
  }
}
</script>

<template>
  <div data-velis-surface="agent-workbench" :class="['flex flex-col gap-4 pb-8']">
    <p :class="['text-sm text-neutral-600 dark:text-neutral-300']">
      {{ t('settings.pages.agent-workbench.description') }}
    </p>

    <div v-if="narrow" :class="['flex flex-wrap gap-2']" role="tablist">
      <Button
        v-for="name in (['sessions', 'timeline', 'diagnosis'] as const)"
        :key="name"
        type="button"
        :variant="panel === name ? 'primary' : 'secondary'"
        :aria-pressed="panel === name"
        @click="showPanel(name)"
      >
        {{ t(`settings.pages.agent-workbench.${name}`) }}
      </Button>
    </div>

    <div :class="['grid gap-4', narrow ? 'grid-cols-1' : 'grid-cols-[14rem_minmax(0,1fr)_18rem]']">
      <section
        v-show="!narrow || panel === 'sessions'"
        data-velis-panel="sessions"
        :class="['flex flex-col gap-2 rounded-xl border border-neutral-200 bg-white/95 p-3 dark:border-neutral-700 dark:bg-neutral-900/95']"
      >
        <h2 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
          {{ t('settings.pages.agent-workbench.sessions') }}
        </h2>
        <p v-if="sessions.length === 0" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
          {{ t('settings.pages.agent-workbench.empty-sessions') }}
        </p>
        <button
          v-for="session in sessions"
          :key="session.sessionId"
          type="button"
          :aria-pressed="session.sessionId === viewingSessionId"
          :class="[
            'rounded-lg px-2 py-2 text-left text-sm',
            session.sessionId === viewingSessionId
              ? 'bg-neutral-200 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-50'
              : 'text-neutral-700 dark:text-neutral-200',
          ]"
          @click="selectSession(session.sessionId)"
        >
          <span :class="['block font-medium']">{{ session.title || session.sessionId }}</span>
          <span v-if="session.sessionId === activeSessionId" :class="['block text-xs']">
            {{ t('settings.pages.agent-workbench.current') }}
          </span>
        </button>
      </section>

      <section
        v-show="!narrow || panel === 'timeline'"
        data-velis-panel="timeline"
        :class="['flex min-w-0 flex-col gap-2 rounded-xl border border-neutral-200 bg-white/95 p-3 dark:border-neutral-700 dark:bg-neutral-900/95']"
      >
        <h2 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
          {{ t('settings.pages.agent-workbench.timeline') }}
        </h2>
        <p v-if="!sessionLoaded" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
          {{ t('settings.pages.agent-workbench.unloaded') }}
        </p>
        <p v-else-if="tasks.length === 0" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
          {{ t('settings.pages.agent-workbench.empty-timeline') }}
        </p>
        <article
          v-for="task in tasks"
          :key="task.id"
          :class="['rounded-lg border border-neutral-200 p-2 dark:border-neutral-700']"
        >
          <button
            type="button"
            :aria-expanded="expandedId === task.id"
            :class="['flex w-full flex-col gap-1 text-left']"
            @click="toggleEvent(task.id)"
          >
            <span :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
              {{ t(`settings.pages.agent-workbench.task-kind.${task.kind}`) }}
              <span v-if="task.toolName"> · {{ task.toolName }}</span>
            </span>
            <span :class="['text-xs text-neutral-600 dark:text-neutral-300']">
              {{ t(`settings.pages.agent-workbench.task.${task.phase}`) }}
              <span v-if="task.errorCode"> · {{ t(`settings.pages.agent-workbench.error-code.${task.errorCode}`) }}</span>
              · {{ formatTime(task.startedAt) }}
              · {{ t('settings.pages.agent-workbench.duration') }} {{ formatDuration(task.durationMs) }}
            </span>
          </button>
          <p v-if="expandedId === task.id" :class="['mt-2 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-200']">
            {{ task.summary }}
          </p>
        </article>
      </section>

      <section
        v-show="!narrow || panel === 'diagnosis'"
        data-velis-panel="diagnosis"
        :class="['flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white/95 p-3 dark:border-neutral-700 dark:bg-neutral-900/95']"
      >
        <h2 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
          {{ t('settings.pages.agent-workbench.diagnosis') }}
        </h2>
        <div data-velis-memory-activity>
          <h3 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
            {{ t('settings.pages.agent-workbench.memory') }}
          </h3>
          <p v-if="memoryUnavailable" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
            {{ t('settings.pages.agent-workbench.memory-unavailable') }}
          </p>
          <p v-else-if="memoryEntries.length === 0" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
            {{ t('settings.pages.agent-workbench.memory-empty') }}
          </p>
          <ul v-else :class="['flex flex-col gap-1 text-sm']">
            <li v-for="(entry, index) in memoryEntries" :key="`${entry.sessionId}-${entry.memoryId ?? entry.reason}-${index}`" :class="['break-all']">
              {{ entry.action }} · {{ entry.reason }}<span v-if="entry.memoryId"> · {{ entry.memoryId }}</span>
            </li>
          </ul>
        </div>
        <div data-velis-latency>
          <h3 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
            {{ t('settings.pages.agent-workbench.latency.title') }}
          </h3>
          <p :class="['text-sm text-neutral-700 dark:text-neutral-200']">
            {{ t(`settings.pages.agent-workbench.latency.warm.${voiceWarm.phase}`) }}
            · {{ voiceWarm.voiceId || t('settings.pages.agent-workbench.none') }}
            · {{ t('settings.pages.agent-workbench.latency.warm.weights') }}
            · {{ voiceWarm.weightsReady ? t('settings.pages.agent-workbench.latency.warm.weights-ready') : t('settings.pages.agent-workbench.latency.warm.weights-missing') }}
            · {{ t('settings.pages.agent-workbench.latency.warm.path') }}
            · {{ voiceWarm.warmPath === null ? t('settings.pages.agent-workbench.none') : voiceWarm.warmPath ? t('settings.pages.agent-workbench.latency.warm.hit') : t('settings.pages.agent-workbench.latency.warm.miss') }}
          </p>
          <p v-if="latencyNewest.length === 0" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
            {{ t('settings.pages.agent-workbench.latency.empty') }}
          </p>
          <article
            v-for="turn in latencyNewest"
            :key="turn.turnId"
            :class="['mt-2 rounded-lg border border-neutral-200 p-2 dark:border-neutral-700']"
          >
            <p :class="['break-all text-sm text-neutral-800 dark:text-neutral-100']">
              {{ turn.turnId }}
            </p>
            <ul :class="['mt-1 flex flex-col gap-1 text-sm text-neutral-700 dark:text-neutral-200']">
              <li v-for="name in latencyMetricNames" :key="name">
                {{ t(`settings.pages.agent-workbench.latency.metric.${name}`) }}
                · {{ formatDuration(turn.metrics[name]) }}
              </li>
            </ul>
            <p :class="['mt-1 text-xs text-neutral-600 dark:text-neutral-300']">
              {{ t('settings.pages.agent-workbench.latency.speech-text') }}
              · {{ t(`settings.pages.agent-workbench.latency.source.${turn.speechTextSource ?? 'none'}`) }}
              · {{ t('settings.pages.agent-workbench.latency.voice-start') }}
              · {{ turn.voiceStart ? t(`settings.pages.agent-workbench.latency.voice.${turn.voiceStart}`) : t('settings.pages.agent-workbench.none') }}
              · {{ t('settings.pages.agent-workbench.latency.speech') }}
              · {{ turn.speechOutcome ? t(`settings.pages.agent-workbench.latency.outcome.${turn.speechOutcome}`) : t('settings.pages.agent-workbench.none') }}
              · {{ t('settings.pages.agent-workbench.latency.warm.path') }}
              · {{ turn.warmPath === undefined ? t('settings.pages.agent-workbench.none') : turn.warmPath ? t('settings.pages.agent-workbench.latency.warm.hit') : t('settings.pages.agent-workbench.latency.warm.miss') }}
            </p>
            <p v-if="turn.gaps.length > 0" :class="['mt-1 text-xs text-neutral-600 dark:text-neutral-300']">
              {{ t('settings.pages.agent-workbench.latency.gaps') }} · {{ turn.gaps.join(', ') }}
            </p>
          </article>
        </div>
        <div data-velis-perception-trace>
          <h3 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
            {{ t('settings.pages.agent-workbench.perception.title') }}
          </h3>
          <div :class="['mt-2', 'flex flex-wrap gap-2']">
            <Button
              type="button"
              data-velis-perception-browser
              :data-state="perceptionPermissions.browser"
              @click="togglePerception('browser')"
            >
              {{ t('settings.pages.agent-workbench.perception.browser') }}
              · {{ t(`settings.pages.agent-workbench.perception.${perceptionPermissions.browser}`) }}
            </Button>
            <Button
              type="button"
              data-velis-perception-screen
              :data-state="perceptionPermissions.screen"
              @click="togglePerception('screen')"
            >
              {{ t('settings.pages.agent-workbench.perception.screen') }}
              · {{ t(`settings.pages.agent-workbench.perception.${perceptionPermissions.screen}`) }}
            </Button>
          </div>
          <p v-if="perceptionTraces.length === 0" :class="['mt-2', 'text-sm text-neutral-600 dark:text-neutral-300']">
            {{ t('settings.pages.agent-workbench.perception.empty') }}
          </p>
          <ul v-else :class="['mt-2', 'flex flex-col gap-1', 'text-sm']">
            <li v-for="(entry, index) in perceptionTraces" :key="`${entry.kind}-${entry.capturedAt ?? index}-${index}`" :class="['break-all']">
              {{ t(`settings.pages.agent-workbench.perception.kind.${entry.kind}`) }}
              · {{ entry.source }}
              · {{ t(`settings.pages.agent-workbench.perception.reason.${entry.reason}`) }}
              · {{ entry.approximateTokens }} {{ t('settings.pages.agent-workbench.perception.tokens') }}
              · {{ formatTime(entry.capturedAt) }}
            </li>
          </ul>
        </div>
        <div data-velis-attention-trace>
          <h3 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
            {{ t('settings.pages.agent-workbench.attention.title') }}
          </h3>
          <p v-if="attentionTraces.length === 0" :class="['mt-2', 'text-sm text-neutral-600 dark:text-neutral-300']">
            {{ t('settings.pages.agent-workbench.attention.empty') }}
          </p>
          <ul v-else :class="['mt-2', 'flex flex-col gap-1', 'text-sm']">
            <li v-for="(entry, index) in attentionTraces" :key="`${entry.event.event_id}-${index}`" :class="['break-all']">
              {{ formatAttentionTrace(entry) }}
            </li>
          </ul>
          <h3 :class="['mt-3', 'text-sm font-medium text-neutral-800 dark:text-neutral-100']">
            {{ t('settings.pages.agent-workbench.attention.badcases') }}
          </h3>
          <ul :class="['mt-2', 'flex flex-col gap-1', 'text-sm']">
            <li v-for="kind in attentionBadcaseKinds" :key="kind" :data-velis-attention-badcase="kind" :class="['break-all']">
              {{ t(`settings.pages.agent-workbench.attention.kind.${kind}`) }}
              · {{ attentionBadcases[kind].length }}
            </li>
          </ul>
        </div>
        <div data-velis-capability-trace>
          <h3 :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
            {{ t('settings.pages.agent-workbench.capability.title') }}
          </h3>
          <p v-if="capabilityTraces.length === 0" :class="['mt-2', 'text-sm text-neutral-600 dark:text-neutral-300']">
            {{ t('settings.pages.agent-workbench.capability.empty') }}
          </p>
          <ul v-else :class="['mt-2', 'flex flex-col gap-1', 'text-sm']">
            <li v-for="(entry, index) in capabilityTraces" :key="`${entry.candidateDomains.join('-')}-${index}`" :class="['break-all']">
              {{ formatCapabilityTrace(entry) }}
            </li>
          </ul>
        </div>
        <dl :class="['flex flex-col gap-2 text-sm']">
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.provider') }}
            </dt>
            <dd>{{ connection.providerLabel.value || t('settings.pages.agent-workbench.none') }}</dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.model') }}
            </dt>
            <dd>{{ connection.modelLabel.value || t('settings.pages.agent-workbench.none') }}</dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.phase') }}
            </dt>
            <dd>{{ t(`settings.pages.chat-connection.status.${connection.phase.value}`) }}</dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.character') }}
            </dt>
            <dd>{{ activeCard?.name || activeCardId || t('settings.pages.agent-workbench.none') }}</dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.speech') }}
            </dt>
            <dd>
              {{ activeSpeechProvider || t('settings.pages.agent-workbench.none') }}
              <span v-if="activeSpeechModel"> / {{ activeSpeechModel }}</span>
              <span v-if="activeSpeechVoiceId"> / {{ activeSpeechVoiceId }}</span>
              · {{ speechReady ? t('settings.pages.agent-workbench.voice-ready') : t('settings.pages.agent-workbench.voice-not-ready') }}
            </dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.hearing') }}
            </dt>
            <dd>
              {{ activeTranscriptionProvider || t('settings.pages.agent-workbench.none') }}
              <span v-if="activeTranscriptionModel"> / {{ activeTranscriptionModel }}</span>
              · {{ hearingReady ? t('settings.pages.agent-workbench.voice-ready') : t('settings.pages.agent-workbench.voice-not-ready') }}
            </dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.mcp') }}
            </dt>
            <dd v-if="mcpUnavailable">
              {{ t('settings.pages.agent-workbench.mcp-unavailable') }}
            </dd>
            <dd v-else-if="mcpServers.length === 0">
              {{ t('settings.pages.agent-workbench.mcp-empty') }}
            </dd>
            <dd v-else>
              <span v-for="server in mcpServers" :key="server.name" :class="['block']">
                {{ server.name }} · {{ server.state }}
              </span>
            </dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.session') }}
            </dt>
            <dd :class="['break-all']">
              {{ viewingSessionId || t('settings.pages.agent-workbench.none') }}
            </dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.tools') }}
            </dt>
            <dd>{{ toolFacts.length }}</dd>
          </div>
          <div v-if="participantCount > 1">
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.leader') }}
            </dt>
            <dd>
              {{ isLeader ? t('settings.pages.agent-workbench.leader.yes') : t('settings.pages.agent-workbench.leader.no') }}
            </dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.error') }}
            </dt>
            <dd>{{ lastFailure?.summary || t('settings.pages.agent-workbench.none') }}</dd>
          </div>
          <div>
            <dt :class="['text-neutral-500']">
              {{ t('settings.pages.agent-workbench.fields.failed-at') }}
            </dt>
            <dd>{{ formatTime(lastFailure?.startedAt) }}</dd>
          </div>
        </dl>

        <ul :class="['flex flex-col gap-2 text-sm']">
          <li v-for="tool in toolFacts" :key="tool.name">
            {{ tool.name }}
            · {{ t(`settings.pages.agent-workbench.risk.${tool.risk}`) }}
            · {{ t(`settings.pages.agent-workbench.confirmation.${tool.confirmation}`) }}
          </li>
        </ul>

        <RouterLink
          to="/settings/chat-connection"
          :class="['text-sm text-primary-600 underline dark:text-primary-300']"
        >
          {{ t('settings.pages.agent-workbench.open-connection') }}
        </RouterLink>

        <div :class="['flex flex-col gap-2']">
          <p v-if="exportArmed" :class="['text-sm text-neutral-700 dark:text-neutral-200']">
            {{ t('settings.pages.agent-workbench.export-scope') }}
          </p>
          <div :class="['flex flex-wrap gap-2']">
            <Button v-if="!exportArmed" type="button" @click="exportArmed = true">
              {{ t('settings.pages.agent-workbench.export') }}
            </Button>
            <Button v-if="exportArmed" type="button" variant="primary" @click="copyExport">
              {{ t('settings.pages.agent-workbench.export-copy') }}
            </Button>
            <Button v-if="exportArmed" type="button" @click="exportArmed = false">
              {{ t('settings.pages.agent-workbench.export-cancel') }}
            </Button>
          </div>
          <p v-if="exportNotice" :class="['text-sm text-neutral-600 dark:text-neutral-300']">
            {{ exportNotice }}
          </p>
        </div>
      </section>
    </div>
  </div>
</template>
